import { Component, type ReactNode } from "react";

/** A lazily loaded page whose chunk is gone: the console was redeployed while
 *  this tab held the old build, and its content-hashed files no longer exist. */
export function isStaleChunk(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(text);
}

const RELOADED = "heimdall.chunk-reload";

export default class RouteBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: unknown) {
    // Reload once to pick up the new build; never loop if the chunk is
    // genuinely missing. The marker is cleared after a successful load.
    if (!isStaleChunk(error)) return;
    try {
      if (sessionStorage.getItem(RELOADED)) return;
      sessionStorage.setItem(RELOADED, String(Date.now()));
    } catch { return; }
    window.location.reload();
  }
  private settle?: ReturnType<typeof setTimeout>;
  componentDidMount() {
    // Forget the reload only once the page has stayed up; clearing it at mount
    // would let a genuinely missing chunk reload forever.
    this.settle = setTimeout(() => { try { sessionStorage.removeItem(RELOADED); } catch { /* storage unavailable */ } }, 15_000);
  }
  componentWillUnmount() { clearTimeout(this.settle); }
  render() { return this.state.failed ? <div className="banner error" role="alert">This page could not load. Your URL selections are preserved. <button onClick={()=>window.location.reload()}>Reload page</button></div> : this.props.children; }
}
